/**
 * Alias-stable ordering (must-not #5, region-map.md must-not 5).
 *
 * > **A phantom must be indistinguishable from a real contact — same component,
 * > same ordering key.**
 *
 * Array position is a channel. `contacts[]` arrives in whatever order the
 * projection built it, and a contact that moves up the list when another is
 * destroyed has told the viewer something the projection deliberately withheld:
 * that a *different* contact stopped existing. Sorting by array index also gives
 * phantoms different position statistics from real contacts as soon as the two
 * are appended by different code paths, and players learn to spot spoofs by
 * where they sit.
 *
 * So every list of contacts — in a rail, on a map, in a compare table — is
 * ordered by a hash of the **per-observer alias** and nothing else. The alias is
 * already salted per observer (M2-D2), so the order is stable for one viewer for
 * the life of a game and uncorrelated between the two teams.
 *
 * Phases 5 and 6 lift this for marker placement, which is the case that made it
 * a rule: a destroyed unit must not reflow the stack and lose everyone their
 * visual anchor mid-turn.
 */

/**
 * FNV-1a, 32-bit.
 *
 * Chosen for being short, dependency-free and *fixed*: the value has to be the
 * same in the rail, on the map and in a test, and `String.prototype` offers
 * nothing that is. It is not a security hash and does not need to be — the input
 * is already the opaque handle.
 */
export function hashAlias(alias: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < alias.length; i += 1) {
    hash ^= alias.charCodeAt(i);
    // `Math.imul` because the 32-bit multiply overflows a double.
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * A total order over aliases.
 *
 * Ties break on the alias itself rather than on array position, because a tie
 * broken by position reintroduces exactly the channel the hash exists to close.
 */
export function compareAliases(a: string, b: string): number {
  const ha = hashAlias(a);
  const hb = hashAlias(b);
  if (ha !== hb) return ha - hb;
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Sorted copy. Never sorts in place — the view is frozen and shared. */
export function byAlias<T>(items: readonly T[], aliasOf: (item: T) => string): readonly T[] {
  return [...items].sort((a, b) => compareAliases(aliasOf(a), aliasOf(b)));
}
